/**
 * Reading an uploaded file, entirely in the browser. The PDF and spreadsheet
 * readers are loaded only when needed, so the first page load stays light.
 */

import { extractCandidates, looksScanned } from '../documents/extract.js';
import { newId, nowIso, type DocKind, type DocumentRecord } from '../workspace/schema.js';
import { saveDocBlob } from '../workspace/store.js';

export const MAX_BYTES = 25 * 1024 * 1024;
export const ACCEPT = '.pdf,.csv,.xlsx,.xls';

type Kind = 'pdf' | 'sheet' | 'other';

function kindOf(file: File): Kind {
  const n = file.name.toLowerCase();
  if (file.type === 'application/pdf' || n.endsWith('.pdf')) return 'pdf';
  if (/\.(csv|xlsx|xls)$/.test(n)) return 'sheet';
  return 'other';
}

export async function ingest(file: File, docKind: DocKind): Promise<DocumentRecord> {
  const base: DocumentRecord = {
    id: newId('doc'),
    name: file.name,
    mime: file.type || 'application/octet-stream',
    size: file.size,
    kind: docKind,
    addedAt: nowIso(),
    status: 'reading',
    candidates: [],
  };
  if (file.size > MAX_BYTES) {
    return { ...base, status: 'error', statusDetail: 'This file is larger than 25 MB. Try exporting just the pages you need.' };
  }
  const k = kindOf(file);
  if (k === 'other') {
    return {
      ...base,
      status: 'unsupported',
      statusDetail: 'Images and word-processor files cannot be read yet. Upload a PDF, Excel or CSV export, or enter the figures by hand.',
    };
  }

  await saveDocBlob(base.id, file);
  const data = await file.arrayBuffer();
  try {
    if (k === 'pdf') {
      const { readPdf } = await import('../documents/readPdf.js');
      const { lines, pageCount, chars } = await readPdf(data);
      if (looksScanned(chars, pageCount)) {
        return {
          ...base,
          status: 'no-text',
          pageCount,
          statusDetail: 'This looks like a scan or photo, so there is no text to read. Enter its figures by hand.',
        };
      }
      const candidates = extractCandidates(lines);
      return {
        ...base,
        status: 'ready',
        pageCount,
        candidates,
        ...(candidates.length === 0 ? { statusDetail: 'Read successfully, but no recognisable financial line items were found.' } : {}),
      };
    }
    const { readSheet } = await import('../documents/readSheet.js');
    const candidates = extractCandidates(readSheet(data));
    return {
      ...base,
      status: 'ready',
      candidates,
      ...(candidates.length === 0 ? { statusDetail: 'Read successfully, but no recognisable financial line items were found.' } : {}),
    };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return {
      ...base,
      status: 'error',
      statusDetail: /password/i.test(msg)
        ? 'This PDF is password-protected. Remove the password and upload it again.'
        : 'This file could not be read. It may be damaged or in an unusual format.',
    };
  }
}

export async function openSourcePage(getBlob: (id: string) => Promise<Blob | undefined>, docId: string, page?: number): Promise<boolean> {
  const blob = await getBlob(docId);
  if (!blob) return false;
  const url = URL.createObjectURL(blob);
  window.open(page ? `${url}#page=${page}` : url, '_blank', 'noopener');
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
}
